import {
    Button,
    ButtonDropdown,
    Container,
    Header,
    Pagination,
    SpaceBetween,
    Table,
    TextFilter,
    CollectionPreferences,
} from '@amzn/awsui-components-console';
import _ from 'lodash';
import { useMemo, useReducer } from 'react';
import { useIntl } from 'react-intl';

import { useAttendanceCollection } from '@/data/useAttendanceCollection';
import { useAttendanceData } from '@/data/useAttendanceData';
import { useMarkClassAttendance } from '@/data/useAttendanceManagement';
import { useAppNotifications } from '@/utils/appNotifications';

import { getStringBundle } from './ClassAttendanceList.messages';
import { submitAttendance } from './classAttendanceList.utils';
import {
    getColumnDefinitions,
    getColumnsToDisplay,
    getErrorSection,
} from './ClassAttendanceListPreferences';
import { getEventHandlers, getInitialState, reducer } from './classAttendanceListStateManager';

/**
 * Component to display attendance information from AttendanceManagementService (AMS).
 * Attendance information consists of a list of students and their attendance status for each session.
 * Class attendance is calculated by AMS based on session attendance data.
 * ClassAttendanceList enables authorized users (i.e. instructors) to mark attendance per session for
 * every student.
 * Note that this module does not use Classroom's roster information and retrieves roster directly from AMS.
 */
const ClassAttendanceList = ({ classroom, learningActivity }) => {
    const intl = useIntl();
    const { addNotification } = useAppNotifications();
    const stringBundle = useMemo(() => getStringBundle(intl.formatMessage), [intl]);

    const { attendanceRecords, error, isLoading, refetch } = useAttendanceData(classroom);

    const [attendanceListState, attendanceListStateDispatch] = useReducer(
        reducer,
        { learningActivity },
        getInitialState,
    );
    const markAttendanceFn = useMarkClassAttendance(classroom);

    const eventHandlers = useMemo(() => {
        return getEventHandlers(attendanceListStateDispatch, addNotification);
    }, [attendanceListStateDispatch, addNotification]);

    useMemo(
        () => eventHandlers.onAttendanceRecordLoaded(attendanceRecords, stringBundle),
        [attendanceRecords, eventHandlers, stringBundle],
    );

    const columnDefinitions = useMemo(() => {
        return getColumnDefinitions(
            learningActivity,
            attendanceListState,
            eventHandlers,
            stringBundle,
        );
    }, [learningActivity, eventHandlers, attendanceListState, stringBundle]);

    const columnDisplay = useMemo(() => {
        return getColumnsToDisplay(learningActivity, attendanceListState.editMode);
    }, [learningActivity, attendanceListState.editMode]);

    // Configure table settings (pageSize, pagination, etc) with filtering
    const { displayItems, tableSettings } = useAttendanceCollection(
        attendanceListState.attendanceRecordList,
        {
            filtering: {
                fields: ['email', 'firstName', 'lastName', 'statusLabels'],
            },
            sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
        },
    );

    const actionOptions = buildActionOptions(learningActivity, stringBundle);

    const listProps = {
        attendanceListState,
        displayItems,
        classroom,
        columnDefinitions,
        columnDisplay,
        eventHandlers,
        tableSettings,
        isLoading,
        markAttendanceFn,
        refetch,
        stringBundle,
    };

    if (learningActivity) {
        if (error) {
            return getErrorSection(error, stringBundle);
        } else if (attendanceListState.editMode) {
            return <ClassAttendanceListEditMode {...listProps} actionOptions={actionOptions} />;
        } else {
            return <ClassAttendanceListViewMode {...listProps} />;
        }
    } else {
        return null;
    }
};

const ClassAttendanceListViewMode = ({
    attendanceListState,
    displayItems,
    columnDefinitions,
    columnDisplay,
    eventHandlers,
    tableSettings,
    isLoading,
    refetch,
    stringBundle,
}) => {
    const isLoadingAttendance = isLoading && (!displayItems || displayItems.length === 0);

    return (
        <Container data-testid='attendance-table-container'>
            <Table
                {...tableSettings.collectionProps}
                data-testid='attendance-table'
                header={
                    <SpaceBetween size='m'>
                        <Header
                            variant='h1'
                            data-testid='attendance-header'
                            counter={
                                attendanceListState.learnersWithSessionAttendanceChanges.size > 0
                                    ? `(${stringBundle.counterText(
                                          attendanceListState.learnersWithSessionAttendanceChanges
                                              .size,
                                      )})`
                                    : ''
                            }
                            actions={
                                <SpaceBetween direction='horizontal' size='xs'>
                                    <Button
                                        variant='normal'
                                        onClick={() => {
                                            refetch();
                                        }}
                                        disabled={isLoading}
                                        iconName='refresh'
                                        data-testid='refresh-button'
                                    />
                                    <Button
                                        variant='primary'
                                        onClick={() => eventHandlers.onListModeChangeToEdit()}
                                        disabled={isLoading}
                                        data-testid='update-attendance-button'
                                    >
                                        Update Attendance
                                    </Button>
                                </SpaceBetween>
                            }
                        >
                            <span data-testid='attendance-header-text'>
                                {stringBundle.header(displayItems.length)}
                            </span>
                        </Header>
                    </SpaceBetween>
                }
                columnDefinitions={columnDefinitions}
                columnDisplay={columnDisplay}
                items={displayItems}
                loading={isLoadingAttendance || attendanceListState.attendanceUpdateInProgress}
                loadingText={
                    attendanceListState.attendanceUpdateInProgress
                        ? stringBundle.markAttendanceInProgressText()
                        : stringBundle.loadingText()
                }
                stripedRows={true}
                filter={<TextFilter {...tableSettings.filterProps} />}
                pagination={<Pagination {...tableSettings.paginationProps} />}
                preferences={<CollectionPreferences {...tableSettings.collectionPreferenceProps} />}
            />
        </Container>
    );
};

const ClassAttendanceListEditMode = ({
    actionOptions,
    attendanceListState,
    displayItems,
    columnDefinitions,
    columnDisplay,
    eventHandlers,
    tableSettings,
    isLoading,
    refetch,
    stringBundle,
    markAttendanceFn,
}) => {
    const isLoadingAttendance = isLoading && (!displayItems || displayItems.length === 0);

    return (
        <Container data-testid='attendance-table-container-edit'>
            <Table
                {...tableSettings.collectionProps}
                data-testid='attendance-table-edit'
                header={
                    <SpaceBetween size='m'>
                        <Header
                            variant='h1'
                            data-testid='attendance-header-edit'
                            description={stringBundle.editModeDescription()}
                            counter={
                                attendanceListState.learnersWithSessionAttendanceChanges.size > 0
                                    ? `(${stringBundle.counterText(
                                          attendanceListState.learnersWithSessionAttendanceChanges
                                              .size,
                                      )})`
                                    : ''
                            }
                            actions={
                                <SpaceBetween direction='horizontal' size='xs'>
                                    <ButtonDropdown
                                        items={actionOptions}
                                        onItemClick={(event) =>
                                            eventHandlers.onBulkActionClicked(
                                                event,
                                                parseAttendanceStatusWithDeliverySession(
                                                    event.detail.id,
                                                ),
                                                tableSettings.collectionProps.selectedItems,
                                                stringBundle,
                                            )
                                        }
                                        disabled={attendanceListState.attendanceUpdateInProgress}
                                        data-testid='bulk-actions-dropdown'
                                    >
                                        Actions
                                    </ButtonDropdown>
                                    <Button
                                        variant='primary'
                                        onClick={() =>
                                            submitAttendance({
                                                markAttendanceFn,
                                                attendanceState: attendanceListState,
                                                eventHandlers,
                                                attendanceRefetcher: refetch,
                                                stringBundle,
                                            })
                                        }
                                        disabled={
                                            _.isEmpty(
                                                attendanceListState.learnersWithSessionAttendanceChanges,
                                            ) || attendanceListState.attendanceUpdateInProgress
                                        }
                                        data-testid='submit-attendance-button'
                                    >
                                        Submit
                                    </Button>
                                    <Button
                                        onClick={() => eventHandlers.onListModeChangeToView()}
                                        disabled={attendanceListState.attendanceUpdateInProgress}
                                        data-testid='cancel-attendance-button'
                                    >
                                        Cancel
                                    </Button>
                                </SpaceBetween>
                            }
                        >
                            {stringBundle.titleEditMode()}
                        </Header>
                    </SpaceBetween>
                }
                columnDefinitions={columnDefinitions}
                columnDisplay={columnDisplay}
                items={displayItems}
                loading={isLoadingAttendance || attendanceListState.attendanceUpdateInProgress}
                loadingText={
                    attendanceListState.attendanceUpdateInProgress
                        ? stringBundle.markAttendanceInProgressText()
                        : stringBundle.loadingText()
                }
                selectionType='multi'
                stripedRows={true}
                filter={<TextFilter {...tableSettings.filterProps} />}
                pagination={<Pagination {...tableSettings.paginationProps} />}
                preferences={<CollectionPreferences {...tableSettings.collectionPreferenceProps} />}
            />
        </Container>
    );
};

const buildActionOptions = (learnerActivity, stringBundle) => {
    return _.map(learnerActivity.delivery_sessions, (deliverySession, index) => {
        return {
            id: `session${index + 1}`,
            text: stringBundle.sessionNth(index + 1),
            items: [
                {
                    text: stringBundle.statusAttended(),
                    id: buildAttendanceStatusWithDeliverySession(deliverySession.id, 'ATTENDED'),
                },
                {
                    text: stringBundle.statusPartiallyAttended(),
                    id: buildAttendanceStatusWithDeliverySession(
                        deliverySession.id,
                        'PARTIALLY_ATTENDED',
                    ),
                },
                {
                    text: stringBundle.statusNoShow(),
                    id: buildAttendanceStatusWithDeliverySession(deliverySession.id, 'NO_SHOW'),
                },
            ],
        };
    });
};

const buildAttendanceStatusWithDeliverySession = (deliverySessionId, attendanceStatus) => {
    return `${deliverySessionId}::${attendanceStatus}`;
};

const parseAttendanceStatusWithDeliverySession = (attendanceStatusWithDeliverySession) => {
    const [deliverySessionId, attendanceStatus] = _.split(
        attendanceStatusWithDeliverySession,
        '::',
        2,
    );
    return {
        deliverySessionId,
        attendanceStatus,
    };
};

export default ClassAttendanceList;
